import { useMemo, useState } from 'react';
import { ReactComponent as PlusIcon } from '@elementary/common-ui/assets/plus-2.svg';
import {
  Autocomplete,
  Button,
  FlexColumn,
} from '@elementary/common-ui/components';
import { Divider, Skeleton } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { METADATA_VALUES } from '@elementary/common-ui/constants/queryKeys';
import { useFormContext } from 'react-hook-form';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import * as api from 'shared/services/api';
import { BulkEditArrayProperty, BulkEditFormData } from '../types';
import ValueEditItem from './ValueEditItem';

type Props = {
  editProperty: BulkEditArrayProperty;
  isLoading?: boolean;
};

const ValuesControlList = ({ editProperty, isLoading }: Props) => {
  const { watch, setValue } = useFormContext<BulkEditFormData>();
  const { tableData, initialTableData } = watch();
  const selectedEnvId = useSelectedEnv();

  const [newTag, setNewTag] = useState(false);

  const { data: metadataData } = useQuery(
    [METADATA_VALUES, selectedEnvId],
    () => api.fetchExtendedMetadataValueOptions(selectedEnvId!),
  );

  const initialTags = useMemo(
    () =>
      new Set(initialTableData.flatMap((model) => model[editProperty] || [])),
    [editProperty, initialTableData],
  );

  const currentTags = useMemo(
    () => new Set(tableData.flatMap((model) => model[editProperty] || [])),
    [editProperty, tableData],
  );

  const newTags = useMemo(
    () =>
      [...currentTags].filter((tag) =>
        [...initialTags].every((initialTag) => initialTag !== tag),
      ),
    [currentTags, initialTags],
  );

  const deletedTags = useMemo(
    () =>
      [...initialTags].filter((tag) =>
        [...currentTags].every((curTag) => curTag !== tag),
      ),
    [currentTags, initialTags],
  );

  const commonTags = useMemo(
    () =>
      [...currentTags].filter((tag) =>
        tableData.every((model) => model[editProperty]?.includes(tag)),
      ),
    [currentTags, editProperty, tableData],
  );

  const partialTags = useMemo(
    () => [...currentTags].filter((tag) => !commonTags.includes(tag)),
    [commonTags, currentTags],
  );

  const sortedTags = useMemo(
    () =>
      [...commonTags, ...partialTags, ...deletedTags]
        .sort((a, b) => a.localeCompare(b))
        .map((tag) => ({
          value: tag,
          isPartial: partialTags.includes(tag),
          isNew: newTags.includes(tag),
          isDeleted: deletedTags.includes(tag),
        })),
    [commonTags, deletedTags, newTags, partialTags],
  );

  const handleEdit = (val: string, newVal: string) => {
    const newTableData = tableData.map((model) => ({
      ...model,
      [editProperty]: model[editProperty].map((tag) =>
        tag === val ? newVal : tag,
      ),
    }));
    setValue('tableData', newTableData);
  };

  const handleApplyForAll = (val: string) => {
    const newTableData = tableData.map((model) => ({
      ...model,
      [editProperty]: model[editProperty].includes(val)
        ? model[editProperty]
        : [...model[editProperty], val],
    }));
    setValue('tableData', newTableData);
  };

  const handleDelete = (val: string) => {
    const newTableData = tableData.map((model) => ({
      ...model,
      [editProperty]: model[editProperty].filter((tag) => tag !== val),
    }));
    setValue('tableData', newTableData);
  };

  const handleUndo = (val: string) => {
    const modelsToUpdate = initialTableData
      .filter((model) => model[editProperty].includes(val))
      .map((model) => model.unique_id);

    const newTableData = tableData.map((model) => ({
      ...model,
      [editProperty]: modelsToUpdate.includes(model.unique_id)
        ? [...model[editProperty], val]
        : model[editProperty],
    }));
    setValue('tableData', newTableData);
  };

  const autocompleteOptions =
    metadataData?.[editProperty]?.filter(
      (tag) => !sortedTags.some((listTag) => listTag.value === tag),
    ) || [];

  const handleNewTag = (val: string) => {
    handleApplyForAll(val);
    setNewTag(false);
  };

  const addButtonTitle = editProperty === 'tags' ? 'Add tag' : 'Add owner';

  return (
    <FlexColumn
      aria-label="values-control-list"
      sx={{ minWidth: '340px', maxWidth: '340px', overflow: 'auto' }}
      divider={<Divider sx={{ my: 1.5 }} light />}
    >
      {isLoading && (
        <FlexColumn>
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} variant="text" width="160px" height="32px" />
          ))}
        </FlexColumn>
      )}

      {!isLoading && (
        <>
          {sortedTags.map(({ value, isPartial, isNew, isDeleted }, i) => (
            <ValueEditItem
              key={`${value}-${i}`}
              value={value}
              showApplyToAll={isPartial}
              isNew={isNew}
              isDeleted={isDeleted}
              onEdit={(val) => handleEdit(value, val)}
              onDelete={() => handleDelete(value)}
              onUndo={() => handleUndo(value)}
              onApplyForAll={() => handleApplyForAll(value)}
              options={autocompleteOptions}
            />
          ))}

          {!newTag && (
            <Button
              sx={{ width: 'fit-content' }}
              color="secondary"
              startIcon={<PlusIcon />}
              onClick={() => setNewTag(true)}
            >
              {addButtonTitle}
            </Button>
          )}

          {newTag && (
            <Autocomplete
              sx={{
                width: '152px',
              }}
              autoFocus
              freeSolo
              options={autocompleteOptions}
              openOnFocus
              onChange={(_, val) => handleNewTag(val)}
              componentsProps={{ popper: { sx: { width: 'auto !important' } } }}
              onBlur={() => setNewTag(false)}
            />
          )}
        </>
      )}
    </FlexColumn>
  );
};

export default ValuesControlList;
